import { NOT_FOUND } from '@mm/constants/status-codes';
import { Dispatch } from 'redux';
import SlackController from '../controllers/SlackController';
import commonHandler from '../errorHandlers/common-handler';
import { OauthUrlResponse } from '../interfaces/googleSheet';
import { SlackDataType } from '../model/CampaignsModel';
import { activeFacebookPageIdSelector } from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class SlackCRUDActions extends CrudActionContainer {
  retrieveOauthUrlAction: string;
  retrieveAccounts: string;
  retrieveChannels: string;
  revokeAccess: string;
  resetStore: string;

  constructor() {
    super('SLACK');
    this.retrieveOauthUrlAction = this.makeActionKey('RETRIEVED_OAUTH_URL');
    this.retrieveAccounts = this.makeActionKey('RETRIEVED_ACCOUNTS');
    this.retrieveChannels = this.makeActionKey('RETRIEVED_CHANNELS');
    this.revokeAccess = this.makeActionKey('REVOKE_ACCESS');
    this.resetStore = this.makeActionKey('RESET_STORE');
  }

  getOauthUrl(redirectTo: string) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const facebookPageId = activeFacebookPageIdSelector(state);
      dispatch(this.busy(this.retrieveOauthUrlAction));
      try {
        const response: OauthUrlResponse = await SlackController.getOauthUrl(
          redirectTo,
          facebookPageId,
        );
        dispatch({
          type: this.retrieveOauthUrlAction,
        });

        return response.link;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getAccounts(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAccounts));
      try {
        const response = await SlackController.getAccounts(facebookPageId);
        dispatch({
          type: this.retrieveAccounts,
          accounts: response,
        });
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  disconnectAccount(accountId: number, fbPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAccess));
      try {
        const response = await SlackController.revokeAccess(
          accountId,
          fbPageId,
        );

        dispatch({
          type: this.revokeAccess,
        });
        dispatch(this.getAccounts(fbPageId));
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getChannels(teamId: string, facebokPageId: number, force: boolean = false) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveChannels));
      try {
        const response = await SlackController.getChannels(
          teamId,
          facebokPageId,
          force,
        );
        const channels = response?.channels || response;

        dispatch({
          type: this.retrieveChannels,
          channels,
        });
        return channels;
      } catch (error) {
        if (error instanceof Response) {
          if (error.status === NOT_FOUND) {
            return;
          }
        }
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  sendTest(facebookPageId: number, args: SlackDataType) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());

      try {
        return await SlackController.sendTest(facebookPageId, args);
      } catch (e) {
        commonHandler(dispatch)(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  sendConnectionTest(
    connectionId: number,
    facebookPageId: number,
    accountId: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());

      try {
        return await SlackController.sendConnectionTest(
          connectionId,
          facebookPageId,
          accountId,
        );
      } catch (e) {
        commonHandler(dispatch)(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  resetSlackStore() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.resetStore });
    };
  }
}

const slackCRUDActions = new SlackCRUDActions();

export default slackCRUDActions;

export const useSlackCRUDActions = getActionHookBuilder(slackCRUDActions, [
  'getOauthUrl',
  'getAccounts',
  'disconnectAccount',
  'getChannels',
  'resetSlackStore',
  'sendConnectionTest',
  'sendTest',
]);
